import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Trash2,
  Users,
  X,
  Hotel,
  Plane,
  MapPin,
  Utensils,
  User,
  Shield,
  Car,
  MoreHorizontal,
  ChevronDown,
  PlaneTakeoff,
  PlaneLanding,
  Check,
  ArrowLeftRight,
  ArrowRight,
  Building2,
  CreditCard,
  DollarSign,
  Layers,
  Info,
} from 'lucide-react';
import { UseFormReturn, useWatch, Controller } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { AIRPORTS, getAirportByCode } from '@/data/airports';
import { trpc } from '@/lib/trpc';

export const CURRENCIES = [
  { value: 'TRY', label: '₺ TRY', symbol: '₺' },
  { value: 'USD', label: '$ USD', symbol: '$' },
  { value: 'EUR', label: '€ EUR', symbol: '€' },
  { value: 'GBP', label: '£ GBP', symbol: '£' },
];

interface PassengerOption {
  firstName: string;
  lastName: string;
}

interface AccountOption {
  id: string;
  code: string;
  name: string;
  type: string;
  subType?: string | null;
  balance?: number;
  currency?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyForm = UseFormReturn<any>;

interface BookingItemRowProps {
  index: number;
  form: AnyForm;
  passengers: PassengerOption[];
  accounts?: AccountOption[];
  onRemove: () => void;
}

// Payment/Funding types for booking items
const PAYMENT_TYPES: ReadonlyArray<{
  value: string;
  labelKey: string;
  icon: typeof CreditCard;
  accountTypes: readonly string[];
}> = [
  { value: 'COMPANY_CREDIT_CARD', labelKey: 'bookings:items.paymentTypes.companyCreditCard', icon: CreditCard, accountTypes: ['CREDIT_CARD'] },
  { value: 'PARTNER_CREDIT', labelKey: 'bookings:items.paymentTypes.partnerCredit', icon: Building2, accountTypes: [] },
];

const SERVICE_TYPES = [
  { value: 'ACCOMMODATION', labelKey: 'bookings:items.serviceTypes.accommodation', icon: Hotel, color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-950/40' },
  { value: 'TRANSPORT', labelKey: 'bookings:items.serviceTypes.transport', icon: Plane, color: 'text-indigo-600', bg: 'bg-indigo-100 dark:bg-indigo-950/40' },
  { value: 'ACTIVITY', labelKey: 'bookings:items.serviceTypes.activity', icon: MapPin, color: 'text-violet-600', bg: 'bg-violet-100 dark:bg-violet-950/40' },
  { value: 'MEAL', labelKey: 'bookings:items.serviceTypes.meal', icon: Utensils, color: 'text-amber-600', bg: 'bg-amber-100 dark:bg-amber-950/40' },
  { value: 'GUIDE', labelKey: 'bookings:items.serviceTypes.guide', icon: User, color: 'text-emerald-600', bg: 'bg-emerald-100 dark:bg-emerald-950/40' },
  { value: 'INSURANCE', labelKey: 'bookings:items.serviceTypes.insurance', icon: Shield, color: 'text-rose-600', bg: 'bg-rose-100 dark:bg-rose-950/40' },
  { value: 'TRANSFER', labelKey: 'bookings:items.serviceTypes.transfer', icon: Car, color: 'text-teal-600', bg: 'bg-teal-100 dark:bg-teal-950/40' },
  { value: 'OTHER', labelKey: 'bookings:items.serviceTypes.other', icon: MoreHorizontal, color: 'text-slate-600', bg: 'bg-slate-100 dark:bg-slate-950/40' },
] as const;

const SERVICE_NAME_OPTIONS: Record<string, string[]> = {
  ACCOMMODATION: ['hotel', 'resort', 'boutique_hotel', 'apartment', 'villa', 'hostel', 'guesthouse', 'bed_breakfast', 'camping', 'cruise_cabin'],
  TRANSPORT: ['flight_domestic', 'flight_international', 'train', 'bus', 'ferry', 'car_rental', 'motorcycle_rental', 'cruise', 'private_jet', 'helicopter'],
  ACTIVITY: ['city_tour', 'day_trip', 'museum', 'theme_park', 'water_sports', 'adventure', 'cultural', 'cooking_class', 'wine_tasting', 'spa_wellness'],
  MEAL: ['breakfast', 'lunch', 'dinner', 'gala_dinner', 'picnic', 'all_inclusive', 'half_board', 'full_board'],
  GUIDE: ['local_guide', 'tour_leader', 'translator', 'specialist_guide'],
  INSURANCE: ['travel_insurance', 'medical_insurance', 'cancellation_insurance', 'luggage_insurance'],
  TRANSFER: ['airport_transfer', 'hotel_transfer', 'station_transfer', 'port_transfer', 'vip_transfer', 'shuttle'],
  OTHER: ['visa_fee', 'tip', 'equipment_rental', 'miscellaneous'],
};

const isFlightService = (serviceName: string) => {
  return ['flight_domestic', 'flight_international', 'private_jet', 'helicopter'].includes(serviceName);
};

// Map service types to partner types
const SERVICE_TO_PARTNER_TYPE: Record<string, 'HOTEL' | 'AIRLINE' | 'TRANSFER' | 'ACTIVITY' | 'INSURANCE' | 'GUIDE' | 'RESTAURANT' | undefined> = {
  ACCOMMODATION: 'HOTEL',
  TRANSPORT: 'AIRLINE',
  TRANSFER: 'TRANSFER',
  ACTIVITY: 'ACTIVITY',
  INSURANCE: 'INSURANCE',
  GUIDE: 'GUIDE',
  MEAL: 'RESTAURANT',
  OTHER: undefined,
};

// Section header component
function SectionHeader({ icon: Icon, label, colorClass }: { icon: typeof Info; label: string; colorClass?: string }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <Icon className={cn('h-3.5 w-3.5', colorClass || 'text-muted-foreground')} />
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
    </div>
  );
}

// Labeled field wrapper
function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

// Partner Selector
function PartnerSelector({ value, onChange, partnerType }: {
  value: string;
  onChange: (value: string) => void;
  partnerType: 'HOTEL' | 'AIRLINE' | 'TRANSFER' | 'ACTIVITY' | 'INSURANCE' | 'GUIDE' | 'RESTAURANT';
}) {
  const { t } = useTranslation(['bookings']);
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const { data: partnersData } = trpc.partner.list.useQuery({
    page: 1,
    type: partnerType,
    status: 'ACTIVE',
  });

  const partners = partnersData?.partners ?? [];
  const selectedPartner = partners.find(p => p.id === value);

  const filteredPartners = React.useMemo(() => {
    if (!search) return partners;
    const q = search.toLowerCase();
    return partners.filter(p =>
      p.companyName.toLowerCase().includes(q) ||
      p.city?.toLowerCase().includes(q)
    );
  }, [partners, search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className={cn('w-full justify-between font-normal', !value && 'text-muted-foreground')}>
          <div className="flex items-center gap-2 truncate">
            <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            {selectedPartner ? (
              <span className="truncate">{selectedPartner.companyName}</span>
            ) : (
              <span>{t('items.selectPartner')}</span>
            )}
          </div>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={t('items.searchPartner')} value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty className="py-3 text-sm">{t('items.noPartnersFound')}</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="_none"
                onSelect={() => { onChange(''); setOpen(false); setSearch(''); }}
                className="cursor-pointer"
              >
                <Check className={cn('mr-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                <span className="text-muted-foreground">{t('items.noPartner')}</span>
              </CommandItem>
              {filteredPartners.map((partner) => (
                <CommandItem
                  key={partner.id}
                  value={partner.id}
                  onSelect={() => { onChange(partner.id); setOpen(false); setSearch(''); }}
                  className="cursor-pointer"
                >
                  <Check className={cn('mr-2 h-4 w-4', value === partner.id ? 'opacity-100' : 'opacity-0')} />
                  <div className="flex flex-col">
                    <span className="font-medium">{partner.companyName}</span>
                    {partner.city && <span className="text-xs text-muted-foreground">{partner.city}</span>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Airport Selector
function AirportSelector({ value, onChange, placeholder, icon: Icon }: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  icon: typeof PlaneTakeoff;
}) {
  const { t } = useTranslation(['bookings']);
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const selectedAirport = value ? getAirportByCode(value) : null;

  const filteredAirports = React.useMemo(() => {
    if (!search || search.length < 1) {
      return AIRPORTS.filter(a => a.country === 'TR').slice(0, 15);
    }
    const q = search.toLowerCase();
    return AIRPORTS.filter(a =>
      a.code.toLowerCase().includes(q) ||
      a.name.toLowerCase().includes(q) ||
      a.city.toLowerCase().includes(q)
    ).slice(0, 20);
  }, [search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className={cn('w-full justify-between font-normal', !value && 'text-muted-foreground')}>
          <div className="flex items-center gap-1.5 truncate">
            <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            {selectedAirport ? (
              <span className="truncate"><span className="font-medium">{selectedAirport.code}</span> - {selectedAirport.city}</span>
            ) : (
              <span>{placeholder}</span>
            )}
          </div>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={t('items.searchAirport')} value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty className="py-3 text-sm">{t('items.noAirportsFound')}</CommandEmpty>
            <CommandGroup>
              {filteredAirports.map((airport) => (
                <CommandItem
                  key={airport.code}
                  value={airport.code}
                  onSelect={() => { onChange(airport.code); setOpen(false); setSearch(''); }}
                  className="cursor-pointer"
                >
                  <Check className={cn('mr-2 h-4 w-4', value === airport.code ? 'opacity-100' : 'opacity-0')} />
                  <span className="w-12 font-medium">{airport.code}</span>
                  <span className="truncate text-muted-foreground">{airport.city}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{airport.country}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function BookingItemRow({ index, form, passengers, accounts = [], onRemove }: BookingItemRowProps) {
  const { t } = useTranslation(['bookings']);
  const { register, setValue, control } = form;
  const [passengerPopoverOpen, setPassengerPopoverOpen] = React.useState(false);
  const [accountPopoverOpen, setAccountPopoverOpen] = React.useState(false);

  const item = useWatch({ control, name: `items.${index}` });
  const selectedPassengerIndexes = item?.passengerIndexes ?? [];
  const serviceType = item?.serviceType || 'ACCOMMODATION';
  const serviceName = item?.name || '';
  const tripType = item?.tripType || 'one_way';
  const isRoundTrip = tripType === 'round_trip';

  const serviceConfig = SERVICE_TYPES.find(s => s.value === serviceType) || SERVICE_TYPES[0];
  const ServiceIcon = serviceConfig.icon;
  const serviceNameKeys = SERVICE_NAME_OPTIONS[serviceType] ?? SERVICE_NAME_OPTIONS['OTHER'] ?? [];
  const isFlight = serviceType === 'TRANSPORT' && isFlightService(serviceName);
  const fromAirport = item?.fromAirport ? getAirportByCode(item.fromAirport) : null;
  const toAirport = item?.toAirport ? getAirportByCode(item.toAirport) : null;

  const quantity = item?.quantity || 1;
  const unitCost = item?.unitCost || 0;
  const serviceFee = item?.serviceFee || 0;
  const sellingPrice = item?.sellingPrice ?? (unitCost + serviceFee);
  const totalPrice = quantity * sellingPrice;
  const totalFee = quantity * serviceFee;
  const currency = item?.currency || 'TRY';
  const currencySymbol = CURRENCIES.find(c => c.value === currency)?.symbol || '';

  // Auto-expand new items (when name is empty)
  const [isExpanded, setIsExpanded] = React.useState(!serviceName);

  // Partner query for collapsed summary
  const partnerType = SERVICE_TO_PARTNER_TYPE[serviceType];
  const { data: partnersData } = trpc.partner.list.useQuery(
    { page: 1, type: partnerType!, status: 'ACTIVE' },
    { enabled: !!partnerType && !!item?.partnerId }
  );
  const partnerName = React.useMemo(() => {
    if (!item?.partnerId || !partnersData?.partners) return '';
    const partner = partnersData.partners.find((p: { id: string; companyName: string }) => p.id === item.partnerId);
    return partner?.companyName || '';
  }, [item?.partnerId, partnersData?.partners]);

  // Summary display values
  const displayServiceName = serviceName
    ? t(`items.serviceNames.${serviceName}`, serviceName)
    : t('items.newItem', 'New Item');

  const routeSummary = React.useMemo(() => {
    if (isFlight && fromAirport && toAirport) {
      return `${fromAirport.code} → ${toAirport.code}`;
    }
    if (serviceType === 'TRANSFER' && (item?.pickupLocation || item?.dropoffLocation)) {
      return [item?.pickupLocation, item?.dropoffLocation].filter(Boolean).join(' → ');
    }
    if (item?.serviceDate) {
      try {
        return new Date(item.serviceDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
      } catch {
        return item.serviceDate;
      }
    }
    return '';
  }, [isFlight, fromAirport, toAirport, serviceType, item?.pickupLocation, item?.dropoffLocation, item?.serviceDate]);

  const togglePassenger = (idx: number) => {
    const current = selectedPassengerIndexes || [];
    setValue(`items.${index}.passengerIndexes`, current.includes(idx) ? current.filter((i: number) => i !== idx) : [...current, idx]);
  };

  const getPassengerName = (idx: number) => {
    const p = passengers[idx];
    return p ? `${p.firstName} ${p.lastName}`.trim() || `#${idx + 1}` : `#${idx + 1}`;
  };

  return (
    <div
      className={cn(
        'rounded-lg border bg-card transition-all duration-200',
        isExpanded
          ? 'border-primary/50 shadow-sm ring-1 ring-primary/10'
          : 'border-border hover:border-border/80 hover:shadow-sm'
      )}
    >
      {/* ============================================================= */}
      {/* COLLAPSED SUMMARY ROW - always visible, clickable             */}
      {/* ============================================================= */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors',
          !isExpanded && 'hover:bg-muted/30'
        )}
      >
        {/* Service icon */}
        <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', serviceConfig.bg)}>
          <ServiceIcon className={cn('h-4 w-4', serviceConfig.color)} />
        </div>

        {/* Name + badge + partner */}
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium">{displayServiceName as string}</span>
              <Badge variant="secondary" className="shrink-0 text-[10px] px-1.5 py-0">
                {t(serviceConfig.labelKey)}
              </Badge>
            </div>
            {partnerName && (
              <span className="block truncate text-xs text-muted-foreground">
                {partnerName}
              </span>
            )}
          </div>

          {/* Route / date summary */}
          {routeSummary && !isExpanded && (
            <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline-flex items-center gap-1">
              {isFlight && <Plane className="h-3 w-3" />}
              {routeSummary}
            </span>
          )}

          {/* Passenger count */}
          {selectedPassengerIndexes.length > 0 && !isExpanded && (
            <Badge variant="outline" className="shrink-0 gap-1 text-[10px]">
              <Users className="h-3 w-3" />
              {selectedPassengerIndexes.length}
            </Badge>
          )}
        </div>

        {/* Price + profit */}
        {!isExpanded && (
          <div className="shrink-0 text-right">
            <div className="text-sm font-semibold tabular-nums">
              {currencySymbol}{totalPrice.toFixed(2)}
            </div>
            <div className={cn(
              'text-[11px] tabular-nums',
              totalFee >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
            )}>
              {totalFee >= 0 ? '+' : ''}{currencySymbol}{totalFee.toFixed(2)}
            </div>
          </div>
        )}

        {/* Chevron indicator */}
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
            isExpanded && 'rotate-180'
          )}
        />

        {/* Delete button */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* ============================================================= */}
      {/* EXPANDED CONTENT                                              */}
      {/* ============================================================= */}
      {isExpanded && (
        <div className="border-t px-5 pb-5 pt-4 space-y-4">

          {/* --------------------------------------------------------- */}
          {/* SECTION 1: Basic Info                                     */}
          {/* --------------------------------------------------------- */}
          <div>
            <SectionHeader icon={Info} label={t('items.basicInfo', 'Basic Info')} />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Field label={t('items.serviceType')}>
                <Controller
                  control={control}
                  name={`items.${index}.serviceType`}
                  render={({ field }) => (
                    <Select value={field.value || 'ACCOMMODATION'} onValueChange={(v) => { field.onChange(v); setValue(`items.${index}.name`, ''); setValue(`items.${index}.tripType`, 'one_way'); }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SERVICE_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{t(type.labelKey)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </Field>

              <Field label={t('items.name')}>
                <Controller
                  control={control}
                  name={`items.${index}.name`}
                  render={({ field }) => (
                    <Select value={field.value || ''} onValueChange={field.onChange}>
                      <SelectTrigger><SelectValue placeholder={t('items.selectService')} /></SelectTrigger>
                      <SelectContent>
                        {serviceNameKeys.map((key) => (
                          <SelectItem key={key} value={key}>{t(`items.serviceNames.${key}`, key)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </Field>

              {/* Partner selector based on service type */}
              {SERVICE_TO_PARTNER_TYPE[serviceType] ? (
                <Field label={t('items.partner')}>
                  <Controller
                    control={control}
                    name={`items.${index}.partnerId`}
                    render={({ field }) => (
                      <PartnerSelector
                        value={field.value || ''}
                        onChange={field.onChange}
                        partnerType={SERVICE_TO_PARTNER_TYPE[serviceType]!}
                      />
                    )}
                  />
                </Field>
              ) : (
                !isFlight && (
                  <Field label={t('items.serviceDate')}>
                    <Input type="date" {...register(`items.${index}.serviceDate`)} />
                  </Field>
                )
              )}

              {/* Dates for non-flight items (when partner exists) */}
              {!isFlight && SERVICE_TO_PARTNER_TYPE[serviceType] ? (
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t('items.serviceDate')}>
                    <Input type="date" {...register(`items.${index}.serviceDate`)} />
                  </Field>
                  <Field label={t('items.endDate')}>
                    <Input type="date" {...register(`items.${index}.endDate`)} />
                  </Field>
                </div>
              ) : !isFlight && !SERVICE_TO_PARTNER_TYPE[serviceType] ? (
                <Field label={t('items.endDate')}>
                  <Input type="date" {...register(`items.${index}.endDate`)} />
                </Field>
              ) : null}
            </div>
          </div>

          {/* --------------------------------------------------------- */}
          {/* SECTION 2: Service-Specific Details (conditional)         */}
          {/* --------------------------------------------------------- */}

          {/* Flight Details */}
          {isFlight && (
            <div>
              <SectionHeader icon={Plane} label={t('items.flightDetails', 'Flight Details')} colorClass="text-indigo-500" />
              <div className="space-y-3 rounded-lg border border-indigo-200 bg-indigo-50/50 p-4 dark:border-indigo-800 dark:bg-indigo-950/20">
                {/* Trip type toggle */}
                <Controller
                  control={control}
                  name={`items.${index}.tripType`}
                  render={({ field }) => (
                    <div className="inline-flex overflow-hidden rounded-lg border">
                      <button type="button" onClick={() => field.onChange('one_way')}
                        className={cn('flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors', field.value !== 'round_trip' ? 'bg-indigo-600 text-white' : 'hover:bg-muted')}>
                        <ArrowRight className="h-4 w-4" />
                        {t('items.oneWay')}
                      </button>
                      <button type="button" onClick={() => field.onChange('round_trip')}
                        className={cn('flex items-center gap-2 border-l px-4 py-2 text-sm font-medium transition-colors', field.value === 'round_trip' ? 'bg-indigo-600 text-white' : 'hover:bg-muted')}>
                        <ArrowLeftRight className="h-4 w-4" />
                        {t('items.roundTrip')}
                      </button>
                    </div>
                  )}
                />

                {/* Outbound flight */}
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <Badge variant="outline" className="bg-white text-xs dark:bg-background">
                      <PlaneTakeoff className="mr-1.5 h-3.5 w-3.5" />
                      {t('items.outboundFlight')}
                    </Badge>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
                    <Field label={t('items.fromAirport')} className="lg:col-span-2">
                      <Controller control={control} name={`items.${index}.fromAirport`}
                        render={({ field }) => (
                          <AirportSelector value={field.value || ''} onChange={field.onChange} placeholder={t('items.selectAirport')} icon={PlaneTakeoff} />
                        )}
                      />
                    </Field>
                    <Field label={t('items.toAirport')} className="lg:col-span-2">
                      <Controller control={control} name={`items.${index}.toAirport`}
                        render={({ field }) => (
                          <AirportSelector value={field.value || ''} onChange={field.onChange} placeholder={t('items.selectAirport')} icon={PlaneLanding} />
                        )}
                      />
                    </Field>
                    <Field label={t('items.flightDate')}>
                      <Input type="date" {...register(`items.${index}.serviceDate`)} />
                    </Field>
                    <Field label={t('items.departureTime')}>
                      <Input type="time" {...register(`items.${index}.departureTime`)} />
                    </Field>
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
                    <Field label={t('items.ticketNumber') + ' *'}>
                      <Input {...register(`items.${index}.ticketNumber`, { required: true })} placeholder="235-1234567890" />
                    </Field>
                    <Field label={t('items.flightNumber')}>
                      <Input {...register(`items.${index}.flightNumber`)} placeholder="TK123" className="uppercase" />
                    </Field>
                    <Field label={t('items.pnr')}>
                      <Input {...register(`items.${index}.pnr`)} placeholder="ABC123" className="uppercase" />
                    </Field>
                  </div>
                </div>

                {/* Return flight (round trip only) */}
                {isRoundTrip && (
                  <div className="rounded-lg bg-amber-50/50 p-4 dark:bg-amber-950/20">
                    <div className="mb-3 flex items-center gap-2">
                      <Badge variant="outline" className="bg-white text-xs dark:bg-background">
                        <PlaneLanding className="mr-1.5 h-3.5 w-3.5" />
                        {t('items.returnFlight')}
                      </Badge>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
                      <Field label={t('items.fromAirport')} className="lg:col-span-2">
                        <Button variant="outline" disabled className="w-full justify-start font-normal">
                          <PlaneTakeoff className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
                          <span className="font-medium">{toAirport?.code || '—'}</span>
                          {toAirport && <span className="ml-1 text-muted-foreground">- {toAirport.city}</span>}
                        </Button>
                      </Field>
                      <Field label={t('items.toAirport')} className="lg:col-span-2">
                        <Button variant="outline" disabled className="w-full justify-start font-normal">
                          <PlaneLanding className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
                          <span className="font-medium">{fromAirport?.code || '—'}</span>
                          {fromAirport && <span className="ml-1 text-muted-foreground">- {fromAirport.city}</span>}
                        </Button>
                      </Field>
                      <Field label={t('items.returnDate')}>
                        <Input type="date" {...register(`items.${index}.endDate`)} />
                      </Field>
                      <Field label={t('items.returnDepartureTime')}>
                        <Input type="time" {...register(`items.${index}.returnDepartureTime`)} />
                      </Field>
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
                      <Field label={t('items.returnTicketNumber') + ' *'}>
                        <Input {...register(`items.${index}.returnTicketNumber`, { required: true })} placeholder="235-1234567891" />
                      </Field>
                      <Field label={t('items.returnFlightNumber')}>
                        <Input {...register(`items.${index}.returnFlightNumber`)} placeholder="TK124" className="uppercase" />
                      </Field>
                      <Field label={t('items.returnPnr')}>
                        <Input {...register(`items.${index}.returnPnr`)} placeholder="XYZ789" className="uppercase" />
                      </Field>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Transfer Details */}
          {serviceType === 'TRANSFER' && (
            <div>
              <SectionHeader icon={Car} label={t('items.route', 'Route')} colorClass="text-teal-500" />
              <div className="rounded-lg border border-teal-200 bg-teal-50/50 p-4 dark:border-teal-800 dark:bg-teal-950/20">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                  <Field label={t('items.pickupLocation')} className="lg:col-span-2">
                    <Input {...register(`items.${index}.pickupLocation`)} placeholder={t('items.pickupPlaceholder')} />
                  </Field>
                  <Field label={t('items.dropoffLocation')} className="lg:col-span-2">
                    <Input {...register(`items.${index}.dropoffLocation`)} placeholder={t('items.dropoffPlaceholder')} />
                  </Field>
                  <Field label={t('items.pickupTime')}>
                    <Input type="time" {...register(`items.${index}.pickupTime`)} />
                  </Field>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                  <Field label={t('items.vehicleType')}>
                    <Controller
                      control={control}
                      name={`items.${index}.vehicleType`}
                      render={({ field }) => (
                        <Select value={field.value || ''} onValueChange={field.onChange}>
                          <SelectTrigger><SelectValue placeholder={t('items.selectVehicle')} /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sedan">{t('items.vehicleTypes.sedan')}</SelectItem>
                            <SelectItem value="minivan">{t('items.vehicleTypes.minivan')}</SelectItem>
                            <SelectItem value="van">{t('items.vehicleTypes.van')}</SelectItem>
                            <SelectItem value="bus">{t('items.vehicleTypes.bus')}</SelectItem>
                            <SelectItem value="luxury">{t('items.vehicleTypes.luxury')}</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </Field>
                </div>
              </div>
            </div>
          )}

          {/* Accommodation Details */}
          {serviceType === 'ACCOMMODATION' && (
            <div>
              <SectionHeader icon={Hotel} label={t('items.roomDetails', 'Room Details')} colorClass="text-blue-500" />
              <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4 dark:border-blue-800 dark:bg-blue-950/20">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <Field label={t('items.roomType')}>
                    <Controller
                      control={control}
                      name={`items.${index}.roomType`}
                      render={({ field }) => (
                        <Select value={field.value || ''} onValueChange={field.onChange}>
                          <SelectTrigger><SelectValue placeholder={t('items.selectRoomType')} /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="standard">{t('items.roomTypes.standard')}</SelectItem>
                            <SelectItem value="superior">{t('items.roomTypes.superior')}</SelectItem>
                            <SelectItem value="deluxe">{t('items.roomTypes.deluxe')}</SelectItem>
                            <SelectItem value="suite">{t('items.roomTypes.suite')}</SelectItem>
                            <SelectItem value="junior_suite">{t('items.roomTypes.juniorSuite')}</SelectItem>
                            <SelectItem value="family">{t('items.roomTypes.family')}</SelectItem>
                            <SelectItem value="studio">{t('items.roomTypes.studio')}</SelectItem>
                            <SelectItem value="apartment">{t('items.roomTypes.apartment')}</SelectItem>
                            <SelectItem value="villa">{t('items.roomTypes.villa')}</SelectItem>
                            <SelectItem value="bungalow">{t('items.roomTypes.bungalow')}</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </Field>

                  <Field label={t('items.boardType')}>
                    <Controller
                      control={control}
                      name={`items.${index}.boardType`}
                      render={({ field }) => (
                        <Select value={field.value || ''} onValueChange={field.onChange}>
                          <SelectTrigger><SelectValue placeholder={t('items.selectBoardType')} /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ROOM_ONLY">{t('items.boardTypes.roomOnly')}</SelectItem>
                            <SelectItem value="BREAKFAST">{t('items.boardTypes.breakfast')}</SelectItem>
                            <SelectItem value="HALF_BOARD">{t('items.boardTypes.halfBoard')}</SelectItem>
                            <SelectItem value="FULL_BOARD">{t('items.boardTypes.fullBoard')}</SelectItem>
                            <SelectItem value="ALL_INCLUSIVE">{t('items.boardTypes.allInclusive')}</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </Field>

                  <Field label={t('items.roomCount')}>
                    <Input
                      type="number"
                      {...register(`items.${index}.roomCount`, { valueAsNumber: true })}
                      min="1"
                      placeholder="1"
                      className="text-center"
                    />
                  </Field>

                  <Field label={t('items.guestCount')}>
                    <Input
                      type="number"
                      {...register(`items.${index}.guestCount`, { valueAsNumber: true })}
                      min="1"
                      placeholder="2"
                      className="text-center"
                    />
                  </Field>
                </div>
              </div>
            </div>
          )}

          {/* --------------------------------------------------------- */}
          {/* SECTION 3: Pricing                                        */}
          {/* --------------------------------------------------------- */}
          <div>
            <SectionHeader icon={DollarSign} label={t('items.pricing', 'Pricing')} colorClass="text-emerald-500" />
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4 dark:border-emerald-800 dark:bg-emerald-950/20">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Field label={t('items.quantity')}>
                  <Input type="number" {...register(`items.${index}.quantity`, { valueAsNumber: true })} min="1" className="text-center" />
                </Field>

                <Field label={t('items.currency')}>
                  <Controller
                    control={control}
                    name={`items.${index}.currency`}
                    render={({ field }) => (
                      <Select value={field.value || 'TRY'} onValueChange={field.onChange}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </Field>

                <Field label={t('items.unitCost')}>
                  <Input type="number" {...register(`items.${index}.unitCost`, { valueAsNumber: true })} step="0.01" placeholder="0.00" className="text-right" />
                </Field>

                <Field label={t('items.sellingPrice')}>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    className="text-right"
                    value={sellingPrice || ''}
                    onChange={(e) => {
                      const newSellingPrice = parseFloat(e.target.value) || 0;
                      setValue(`items.${index}.sellingPrice`, newSellingPrice);
                      const newServiceFee = newSellingPrice - unitCost;
                      setValue(`items.${index}.serviceFee`, newServiceFee);
                    }}
                  />
                </Field>

              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <Field label={t('items.serviceFee')}>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    className={cn('text-right', serviceFee >= 0 ? 'text-emerald-600' : 'text-red-600')}
                    value={serviceFee || ''}
                    onChange={(e) => {
                      const newServiceFee = parseFloat(e.target.value) || 0;
                      setValue(`items.${index}.serviceFee`, newServiceFee);
                      const newSellingPrice = unitCost + newServiceFee;
                      setValue(`items.${index}.sellingPrice`, newSellingPrice);
                    }}
                  />
                </Field>

                <Field label={t('items.total')}>
                  <div className="flex h-9 items-center justify-end rounded-md border bg-white/60 px-3 text-sm font-semibold tabular-nums dark:bg-background/60">
                    {currencySymbol}{totalPrice.toFixed(2)}
                  </div>
                </Field>

                <Field label={t('items.profit')}>
                  <div className={cn('flex h-9 items-center justify-end rounded-md border px-3 text-sm font-semibold tabular-nums',
                    totalFee >= 0 ? 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-700' : 'bg-red-100 text-red-700 border-red-300 dark:bg-red-950/40 dark:text-red-400 dark:border-red-700'
                  )}>
                    {totalFee >= 0 ? '+' : ''}{currencySymbol}{totalFee.toFixed(2)}
                  </div>
                </Field>
              </div>
            </div>
          </div>

          {/* --------------------------------------------------------- */}
          {/* SECTION 4: Bottom Row                                     */}
          {/* --------------------------------------------------------- */}
          <div>
            <SectionHeader icon={Layers} label={t('items.additional', 'Additional')} />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {/* Travellers */}
              <Field label={t('items.travellers')}>
                {passengers.length === 0 ? (
                  <span className="flex h-9 items-center text-sm text-muted-foreground">{t('items.addPassengersFirst')}</span>
                ) : (
                  <div className="space-y-2">
                    <Popover open={passengerPopoverOpen} onOpenChange={setPassengerPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-between font-normal">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            {selectedPassengerIndexes.length === 0
                              ? <span className="text-muted-foreground">{t('items.selectTravellersPlaceholder')}</span>
                              : <span>{selectedPassengerIndexes.length} traveller{selectedPassengerIndexes.length > 1 ? 's' : ''}</span>}
                          </div>
                          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 p-0" align="start">
                        <Command>
                          <CommandList>
                            <CommandGroup>
                              {passengers.map((_, pIdx) => (
                                <CommandItem key={pIdx} onSelect={() => togglePassenger(pIdx)} className="cursor-pointer">
                                  <Checkbox checked={selectedPassengerIndexes.includes(pIdx)} className="mr-2" />
                                  {getPassengerName(pIdx)}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>

                    {selectedPassengerIndexes.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1">
                        {selectedPassengerIndexes.slice(0, 3).map((pIdx: number) => (
                          <Badge key={pIdx} variant="secondary" className="gap-1 px-2 py-0.5">
                            {getPassengerName(pIdx).split(' ')[0]}
                            <button type="button" onClick={() => togglePassenger(pIdx)} className="hover:text-destructive"><X className="h-3 w-3" /></button>
                          </Badge>
                        ))}
                        {selectedPassengerIndexes.length > 3 && (
                          <span className="text-xs text-muted-foreground">+{selectedPassengerIndexes.length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Field>

              {/* Payment Type */}
              <Field label={t('items.paymentType')}>
                <Controller
                  control={control}
                  name={`items.${index}.paymentType`}
                  render={({ field }) => (
                    <Select value={field.value || ''} onValueChange={(v) => {
                      field.onChange(v);
                      setValue(`items.${index}.paymentAccountId`, '');
                      setValue(`items.${index}.fundingSourceId`, '');
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('items.selectPaymentType')} />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_TYPES.map((pt) => (
                          <SelectItem key={pt.value} value={pt.value}>
                            <div className="flex items-center gap-2">
                              <pt.icon className="h-3.5 w-3.5" />
                              {t(pt.labelKey)}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />

                {/* Account selector for company types */}
                {item?.paymentType === 'COMPANY_CREDIT_CARD' && (() => {
                  const paymentTypeConfig = PAYMENT_TYPES.find(pt => pt.value === item?.paymentType);
                  const filteredAccounts = accounts.filter(acc =>
                    acc.subType && paymentTypeConfig?.accountTypes.includes(acc.subType)
                  );
                  return (
                    <div className="mt-2">
                      {filteredAccounts.length > 0 ? (
                        <Controller
                          control={control}
                          name={`items.${index}.paymentAccountId`}
                          render={({ field }) => {
                            const selectedAccount = filteredAccounts.find(acc => acc.id === field.value);
                            return (
                              <Popover open={accountPopoverOpen} onOpenChange={setAccountPopoverOpen}>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" className={cn('w-full justify-between font-normal', !field.value && 'text-muted-foreground')}>
                                    <span className="truncate">
                                      {selectedAccount ? `${selectedAccount.code} - ${selectedAccount.name}` : t('items.selectAccount')}
                                    </span>
                                    <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-80 p-0" align="start">
                                  <Command>
                                    <CommandInput placeholder={t('items.searchAccounts')} />
                                    <CommandList>
                                      <CommandEmpty className="py-3 text-sm">{t('items.noAccountsFound')}</CommandEmpty>
                                      <CommandGroup>
                                        {filteredAccounts.map((acc) => (
                                          <CommandItem
                                            key={acc.id}
                                            onSelect={() => { field.onChange(acc.id); setAccountPopoverOpen(false); }}
                                            className="cursor-pointer"
                                          >
                                            <Check className={cn('mr-2 h-4 w-4', field.value === acc.id ? 'opacity-100' : 'opacity-0')} />
                                            <div className="flex flex-col">
                                              <span className="font-medium">{acc.code} - {acc.name}</span>
                                              {acc.balance !== undefined && (
                                                <span className="text-xs text-muted-foreground">
                                                  {t('items.balance')}: {acc.currency || 'TRY'} {acc.balance.toLocaleString()}
                                                </span>
                                              )}
                                            </div>
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            );
                          }}
                        />
                      ) : (
                        <div className="flex h-9 items-center rounded-md border bg-muted/30 px-3 text-sm text-muted-foreground">
                          {t('items.noAccountsFound')}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Partner Credit info */}
                {item?.paymentType === 'PARTNER_CREDIT' && item?.partnerId && (
                  <div className="mt-2 flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-300">
                    <Building2 className="h-3.5 w-3.5" />
                    <span>{t('items.usingPartnerCredit')}</span>
                  </div>
                )}
              </Field>

              {/* Notes */}
              <Field label={t('items.notes')} className="lg:col-span-2">
                <Input {...register(`items.${index}.description`)} placeholder={t('items.optionalNotes')} />
              </Field>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
